import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSimpleMediaLibrary } from "@/core/MediaLibrary/hooks/useSimpleMediaLibrary";
import { useAppStore } from "@/core/store/useAppStore";
import * as collectionsService from "@/services/collectionsService";
import { MediaItemsGrid } from "./MediaItemsGrid"; // Updated import
import { SelectionPanel } from "./SelectionPanel";
import { MediaCropModal, CropSelection } from "./MediaCropModal";
import { Button } from "../../components/ui/Button";
import { ProgressBar } from "../../components/ui/ProgressBar";
import { MediaStage, PostStatus, ACTIVE_POST_STATUSES } from "@/core/types/mediaTypes";
import { ChevronDownIcon } from "@/components/icons";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";

interface PostCreatorImageSelectorProps {
  postCreator: ReturnType<
    typeof import("./hooks/usePostCreator").usePostCreator
  >;
  /** When the post is being created from an idea, scope the picker to it. */
  linkToIdeaId?: string | null;
}

export const PostCreatorImageSelector: React.FC<
  PostCreatorImageSelectorProps
> = ({ postCreator, linkToIdeaId = null }) => {
  const { selectedClient } = useAppStore();
  const [showUsedImages, setShowUsedImages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Default to the idea's media when launched from one; toggle widens to all.
  const [mediaScope, setMediaScope] = useState<"idea" | "all">(
    linkToIdeaId ? "idea" : "all"
  );

  // The set of media-item ids in the idea's linked collection.
  const { data: ideaMediaIds } = useQuery({
    queryKey: ["idea-collection-item-ids", linkToIdeaId, selectedClient?.id],
    queryFn: async (): Promise<Set<number>> => {
      if (!linkToIdeaId || !selectedClient?.id) return new Set();
      const collection = await collectionsService.getCollectionByIdea(linkToIdeaId);
      if (!collection) return new Set();
      const ids = await collectionsService.getCollectionItemIds(
        selectedClient.id,
        collection.id
      );
      return new Set(ids);
    },
    enabled: Boolean(linkToIdeaId && selectedClient?.id),
    staleTime: 60_000,
  });

  // Use stage filter (approved only) and optionally exclude items in posts
  const stageFilter = useMemo(() => [MediaStage.APPROVED], []);
  const excludePostStatuses = useMemo(() => {
    // If showing used images, don't exclude any post statuses (show all images)
    if (showUsedImages) return undefined;
    // Otherwise exclude images that are part of ANY post (draft, ready, scheduled, published, etc.)
    return Object.values(PostStatus);
  }, [showUsedImages]);

  const mediaLibrary = useSimpleMediaLibrary({
    clientId: selectedClient?.id || '',
    autoLoad: true,
    contentTypes: ["photos", "post_video"],
    stageFilter,
    excludePostStatuses,
  });

  const isLoading = mediaLibrary.isLoading;
  const isStreamingComplete = !mediaLibrary.isLoading && mediaLibrary.hasData;

  const filteredImages = useMemo(() => {
    // Post builder selects 4:5 images and 4:5 post videos together; the
    // user reorders them in the SelectionPanel.
    let selectable = [...mediaLibrary.images, ...(mediaLibrary.postVideos ?? [])];

    // When creating from an idea, default to that idea's media; the toggle
    // widens to all client media.
    if (linkToIdeaId && mediaScope === "idea" && ideaMediaIds) {
      selectable = selectable.filter((item) => ideaMediaIds.has(item.id));
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      selectable = selectable.filter(item =>
        item.originalFilename.toLowerCase().includes(query)
      );
    }

    return selectable;
  }, [
    mediaLibrary.images,
    mediaLibrary.postVideos,
    searchQuery,
    linkToIdeaId,
    mediaScope,
    ideaMediaIds,
  ]);

  const error = mediaLibrary.error;
  const totalCount = filteredImages.length;
  const loadingProgress = mediaLibrary.images.length;
  const imageErrors: string[] = [];

  console.log("PostCreatorImageSelector results:", {
    allMediaItems: mediaLibrary.images.length,
    filteredImages: filteredImages.length,
    isLoading,
    error,
    showUsedImages,
    excludePostStatuses,
    stageFilter,
  });

  const shouldShowProgress = useMemo(
    () =>
      (isLoading && loadingProgress >= 0) ||
      (!isStreamingComplete && loadingProgress > 0),
    [isLoading, loadingProgress, isStreamingComplete],
  );

  // Memoize handlers to prevent flickering
  const handleImageClick = useCallback(
    (image: any) => {
      if (
        !postCreator.isCarousel &&
        postCreator.selectedMediaItems.length === 1
      ) {
        // In single image mode, replace the selection
        postCreator.setSelectedMediaItems([image]);
      } else {
        // In carousel mode or no selection, toggle normally
        const isSelected = postCreator.selectedMediaItems.some(
          (img) => img.id === image.id,
        );

        if (isSelected) {
          // Remove the image
          postCreator.setSelectedMediaItems(
            postCreator.selectedMediaItems.filter((img) => img.id !== image.id),
          );
        } else {
          // Add the image
          if (postCreator.isCarousel) {
            if (postCreator.selectedMediaItems.length < 10) {
              postCreator.setSelectedMediaItems([
                ...postCreator.selectedMediaItems,
                image,
              ]);
            }
          } else {
            postCreator.setSelectedMediaItems([image]);
          }
        }
      }
    },
    [
      postCreator.isCarousel,
      postCreator.selectedMediaItems,
      postCreator.setSelectedMediaItems,
    ],
  );

  const handleClearSelection = useCallback(() => {
    postCreator.setSelectedMediaItems([]);
  }, [postCreator.setSelectedMediaItems]);

  const handleRemoveImage = useCallback(
    (imageId: number) => {
      postCreator.setSelectedMediaItems(
        postCreator.selectedMediaItems.filter((img) => img.id !== imageId),
      );
      // Drop any crop we were holding for this item so it doesn't leak
      // back if the user re-selects the same image later in the session.
      postCreator.setCropForItem(imageId, null);
    },
    [postCreator.selectedMediaItems, postCreator.setSelectedMediaItems, postCreator.setCropForItem],
  );

  const [cropTarget, setCropTarget] = useState<MediaItem | null>(null);

  const handleOpenCrop = useCallback((item: MediaItem) => {
    setCropTarget(item);
  }, []);

  const handleCloseCrop = useCallback(() => {
    setCropTarget(null);
  }, []);

  const handleSaveCrop = useCallback(
    (rect: CropSelection) => {
      if (cropTarget) {
        postCreator.setCropForItem(cropTarget.id, rect);
      }
    },
    [cropTarget, postCreator.setCropForItem],
  );

  const handleClearCrop = useCallback(() => {
    if (cropTarget) {
      postCreator.setCropForItem(cropTarget.id, null);
    }
  }, [cropTarget, postCreator.setCropForItem]);

  const handleReorderImages = useCallback(
    async (newOrder: any[]) => {
      // Update local state immediately for responsive UI
      postCreator.setSelectedMediaItems(newOrder);
      
      // If editing an existing post, persist the reorder to backend
      if (postCreator.editingPost && postCreator.clientId) {
        try {
          const mediaOrder = newOrder.map((item, index) => ({
            id: item.id,
            position: index
          }));
          
          await postCreator.reorderMediaItems(mediaOrder);
          console.log('✅ Media reorder saved successfully');
        } catch (error) {
          console.error('❌ Failed to save media reorder:', error);
          // Optionally revert the local state if API call fails
          // postCreator.setSelectedMediaItems(postCreator.selectedMediaItems);
        }
      }
    },
    [postCreator],
  );

  return (
    <div className="flex flex-col h-full">
      {/* Progress Bar */}
      {shouldShowProgress && (
        <div className="px-6 pt-4">
          <ProgressBar
            current={loadingProgress}
            total={totalCount || undefined}
            label="Loading images"
            errors={imageErrors.length}
            isComplete={isStreamingComplete}
            isActive={shouldShowProgress}
            showCount={true}
          />
        </div>
      )}

      {/* Header */}
      <div className="p-6 border-b space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Selection info */}
            <div className="flex items-center gap-2">
              {postCreator.isCarousel ? (
                <span className="text-sm text-gray-600">
                  Carousel: {postCreator.selectedMediaItems.length}/
                  {postCreator.maxImages} images
                </span>
              ) : (
                <span className="text-sm text-gray-600">Single image mode</span>
              )}
            </div>

            {postCreator.selectedMediaItems.length > 0 && (
              <span className="text-sm font-medium text-blue-600">
                {postCreator.selectedMediaItems.length} selected
              </span>
            )}

            {/* Scope: default to the idea's media, toggle widens to all. */}
            {linkToIdeaId && (
              <div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => setMediaScope("idea")}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    mediaScope === "idea"
                      ? "bg-white text-gray-900 shadow-xs"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  From this idea
                </button>
                <button
                  type="button"
                  onClick={() => setMediaScope("all")}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    mediaScope === "all"
                      ? "bg-white text-gray-900 shadow-xs"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  All media
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {/* Filter Toggle */}
            <div className="flex items-center space-x-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showUsedImages}
                  onChange={(e) => setShowUsedImages(e.target.checked)}
                  className="rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Include media already used in posts</span>
              </label>
            </div>

            {/* Image count */}
            <span className="text-sm text-gray-600">
              {filteredImages.length} image
              {filteredImages.length !== 1 ? "s" : ""}
              {shouldShowProgress && !isStreamingComplete && (
                <span className="text-blue-600"> (loading more...)</span>
              )}
            </span>

            <Button
              variant="ghost"
              size="sm"
              onClick={mediaLibrary.refresh}
              disabled={isLoading}
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Content with side panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Media Items Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && filteredImages.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center text-red-600">
                <p className="mb-2">Failed to load images</p>
                <p className="text-sm">{error}</p>
                <Button variant="ghost" onClick={mediaLibrary.refresh} className="mt-4">
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <>
              <MediaItemsGrid
                images={filteredImages}
                selectedMediaItems={postCreator.selectedMediaItems}
                onImageClick={handleImageClick}
                isLoading={isLoading}
                error={error}
                isCarousel={postCreator.isCarousel}
              />

              {/* Load More Button */}
              {mediaLibrary.hasMore && (
                <div className="flex justify-center py-6 px-6">
                  <button
                    onClick={mediaLibrary.loadMore}
                    disabled={mediaLibrary.isLoadingMore}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200 disabled:text-gray-400"
                  >
                    {mediaLibrary.isLoadingMore ? (
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Loading more...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        Load more
                        <ChevronDownIcon className="w-4 h-4 ml-1" />
                      </span>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right Panel - Selection Panel */}
        <div className="w-80 border-l bg-gray-50 overflow-y-auto">
          <SelectionPanel
            selectedMediaItems={postCreator.selectedMediaItems}
            isCarousel={postCreator.isCarousel}
            onRemove={handleRemoveImage}
            onClear={handleClearSelection}
            onReorder={postCreator.isCarousel ? handleReorderImages : undefined}
            crops={postCreator.crops}
            onCrop={handleOpenCrop}
          />
        </div>
      </div>

      <MediaCropModal
        isOpen={cropTarget !== null}
        mediaItem={cropTarget}
        initialCrop={cropTarget ? postCreator.crops[cropTarget.id] ?? null : null}
        onClose={handleCloseCrop}
        onSave={handleSaveCrop}
        onClear={handleClearCrop}
      />
    </div>
  );
};
